import axios from 'axios';
import { notifications } from '@mantine/notifications';
import { API_TEMPLATES_BASE_URL } from '@/config';
import { useStore } from '@/store';

// Default timeout for templates-API calls. 5s is too aggressive for large
// template payloads and recent-file transfers. Individual requests can still
// override this budget when they need longer.
const DEFAULT_TIMEOUT_MS = 30_000;

const api = axios.create({
  baseURL: API_TEMPLATES_BASE_URL,
  timeout: DEFAULT_TIMEOUT_MS,
  headers: {
    'Content-Type': 'application/json; charset=utf-8',
  },
});

api.interceptors.request.use((config) => {
  try {
    const jwt = useStore.getState().config.getJWT();
    config.headers.Authorization = `Bearer ${jwt}`;
  } catch {
    // JWT not yet set, request proceeds without token
  }
  return config;
});

api.interceptors.response.use(
  (response) => response,
  (error) => {
    const status = error.response?.status;
    // The server's own message, when it sent one. Collapsing every failure into
    // "your session expired" sent people to log in again while the auth service
    // was simply unreachable, which no amount of logging in would fix.
    const serverMessage = error.response?.data?.error as string | undefined;

    if (status === 401) {
      notifications.show({
        title: 'Unauthorized',
        message: serverMessage ?? 'Your session is invalid or has expired.',
        color: 'red',
        autoClose: false,
      });
    }

    if (status === 502 || status === 503) {
      notifications.show({
        title: 'Service unavailable',
        message:
          serverMessage ??
          'The authentication service is not responding. This is not a problem with your account — try again shortly.',
        color: 'orange',
        autoClose: false,
      });
    }

    return Promise.reject(error);
  },
);

export default api;
